import { ZipFile } from "@utils/zip-file";
import { ReadLsDataV2Response, LsRootObject, LsScan } from "@src/api/stagingarea-api/stagingarea-api-types";
import { getEntitiesPath } from "@utils/capture-tree/capture-tree-utils";
import { FileExt } from "@utils/file-ext";

const MS_PER_SECOND = 1_000;

/**
 * FocusFile class extends ZipFile to include scanId and timestamp properties.
 */
export class FocusFile extends ZipFile {
  /** Original ScanID */
  scanId: string;
  /** Timestamp in milliseconds */
  timestamp: number;

  constructor(files: (File | FileExt)[], name: string, scanId: string, timestampSeconds: number) {
    super(files, name);
    this.scanId = scanId;
    // Convert timestamp from seconds to milliseconds
    // to match the expected format in the LsScan object.
    this.timestamp = timestampSeconds * MS_PER_SECOND;
  }
}

/**
 * Creates a dummy LsDataV2 object from a list of FocusFiles
 */
export function createFocusDummyLsDataV2(focusFiles: FocusFile[]): ReadLsDataV2Response {
  // Minimal root object
  const rootObject: LsRootObject = {
    name: "RootObject",
    uuid: "00000000-0000-0000-0000-000000000000",
    revision: "0",
    treeLevel: 0,
    treePath: "root",
    ancestorUuids: [],
  };

  // Create LsScan objects for each FocusFile
  const scans: LsScan[] = focusFiles.map((focusFile) => ({
    uuid: focusFile.scanId,
    // This fallback will make sure, the scan is added to the root.
    parentUuid: "root",
    name: focusFile.name,
    description: "",
    recordingTime: focusFile.timestamp ? new Date(focusFile.timestamp).toISOString() : undefined,
    files: [
      {
        path: focusFile.name,
      },
    ],
    treeLevel: 1,
    treePath: getEntitiesPath([rootObject.name, focusFile.name]),
    ancestorUuids: [rootObject.uuid],
  }));

  // Map from filename (lowercase) to scan
  const scansByFilename: Record<string, LsScan> = {};
  scans.forEach((scan) => {
    scansByFilename[scan.name.toLowerCase()] = scan;
  });

  // Map from uuid to scan
  const scansByUuid: Record<string, LsScan> = {};
  scans.forEach((scan) => {
    scansByUuid[scan.uuid] = scan;
  });

  return {
    rootObject,
    projects: [],
    clusters: [],
    scans,
    edges: [],
    maxTreeLevel: 1,
    treeObjectsByUuid: {
      ...scansByUuid,
    },
    clustersByUuid: {},
    scansByUuid,
    scansByFilename,
  };
}
